import React, { ReactNode, useState, useEffect } from 'react';
import { useBranding } from '../context/BrandingContext';

// ── Design tokens ─────────────────────────────────────────────────────────────
export const C = {
  green: '#0F6E56', greenMid: '#1D9E75', greenLight: '#5DCAA5',
  greenBg: '#E1F5EE', greenPale: '#9FE1CB',
  purple: '#3C3489', purpleBg: '#EEEDFE',
  coral: '#D85A30', yellow: '#EF9F27',
  bg: '#F5F7F5', card: '#FFFFFF',
  border: '#E5E5E5', borderLight: '#F0F0F0',
  text: '#111111', textSub: '#666666', textMuted: '#999999',
};

const base: React.CSSProperties = { fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' };

// ── Button ────────────────────────────────────────────────────────────────────
interface BtnProps { children: ReactNode; onClick?: () => void; variant?: 'primary'|'outline'|'danger'|'ghost'; size?: 'sm'|'md'|'lg'; disabled?: boolean; fullWidth?: boolean; type?: 'button'|'submit'; style?: React.CSSProperties }
export function Btn({ children, onClick, variant='primary', size='md', disabled, fullWidth, type='button', style }: BtnProps) {
  const styles: Record<string, React.CSSProperties> = {
    primary: { background: 'var(--color-button, #1D9E75)', color: '#fff', border: 'none' },
    outline: { background: 'transparent', color: C.text, border: `1px solid ${C.border}` },
    danger: { background: '#D84040', color: '#fff', border: 'none' },
    ghost: { background: 'transparent', color: 'var(--color-button, #1D9E75)', border: 'none' },
  };
  const sizes: Record<string, React.CSSProperties> = {
    sm: { padding: '6px 12px', fontSize: 12 },
    md: { padding: '10px 18px', fontSize: 14 },
    lg: { padding: '14px 24px', fontSize: 15 },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      ...base, ...styles[variant], ...sizes[size],
      borderRadius: 8, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1, width: fullWidth ? '100%' : undefined,
      transition: 'opacity 0.15s',
      ...style,
    }}>{children}</button>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return <div style={{ ...base, background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, ...style }}>{children}</div>;
}

// ── Badge ─────────────────────────────────────────────────────────────────────
const badgeColors: Record<string, [string,string]> = {
  Pending: ['#FFF7E6','#854F0B'], Accepted: ['#E1F5EE','#085041'],
  EnRoute: ['#EEF4FF','#1D4ED8'], Arrived: ['#FFF0F0','#9B1C1C'],
  InProgress: ['#F0FFF4','#166534'], Completed: ['#E1F5EE','#085041'],
  Cancelled: ['#F5F5F5','#666'], Cardboard: ['#EAF3DE','#3B6D11'],
  Furniture: ['#EEF4FF','#1D4ED8'], YardDebris: ['#FFF7E6','#854F0B'],
  General: ['#F5F5F5','#444'],
  Approved: ['#E1F5EE','#085041'], Rejected: ['#FFF0F0','#9B1C1C'],
  Submitted: ['#FFF7E6','#854F0B'], Expired: ['#FFF0F0','#9B1C1C'],
  Draft: ['#F5F5F5','#666'], Sent: ['#EEF4FF','#1D4ED8'], Viewed: ['#EEF4FF','#1D4ED8'],
  Declined: ['#FFF0F0','#9B1C1C'], ChangesRequested: ['#FFF7E6','#854F0B'],
};
export function Badge({ label }: { label: string }) {
  const [bg, color] = badgeColors[label] ?? ['#F5F5F5','#444'];
  return <span style={{ ...base, background: bg, color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{label}</span>;
}

// ── Input ─────────────────────────────────────────────────────────────────────
interface InputProps { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; required?: boolean; }
export function Input({ label, value, onChange, type='text', placeholder, required }: InputProps) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} style={{ ...base, width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, color: C.text, outline: 'none' }} />
    </div>
  );
}

// ── Select ────────────────────────────────────────────────────────────────────
export function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: {value:string;label:string}[] }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ ...base, width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, color: C.text, background: '#fff' }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ── Stars ─────────────────────────────────────────────────────────────────────
export function StarRating({ value, onChange }: { value: number; onChange?: (n: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} onClick={() => onChange?.(n)} style={{ fontSize: 22, cursor: onChange ? 'pointer' : 'default', color: n <= value ? C.yellow : C.border }}>{n <= value ? '★' : '☆'}</span>
      ))}
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.greenMid, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;
}

// ── Alert ─────────────────────────────────────────────────────────────────────
export function Alert({ message, type='error' }: { message: string; type?: 'error'|'success' }) {
  if (!message) return null;
  const [bg, color] = type === 'success' ? [C.greenBg, C.green] : ['#FFF0F0','#9B1C1C'];
  return <div style={{ background: bg, color, border: `1px solid ${color}22`, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>{message}</div>;
}

// ── Stat card ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, color }: { label: string; value: string|number; sub?: string; color?: string }) {
  return (
    <Card style={{ flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || C.text }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.textSub, marginTop: 4 }}>{sub}</div>}
    </Card>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────
export function PageShell({ children, title, nav }: { children: ReactNode; title: string; nav?: ReactNode }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const branding = useBranding();
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const navText = branding.navTextColor ?? '#ffffff';
  const logoInner = branding.logoUrl
    ? <img src={branding.logoUrl} alt={branding.appName} style={{ height: 36, maxWidth: 140, objectFit: 'contain', flexShrink: 0, display: 'block' }} />
    : <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontStyle: 'italic', fontSize: isMobile ? 15 : 18, letterSpacing: 1, flexShrink: 0, color: navText }}>
        {branding.appName}
      </span>;
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: branding.primaryColor, padding: '0 12px 0 16px', display: 'flex', alignItems: 'center', gap: 10, height: 56, flexShrink: 0 }}>
        {branding.logoLinkUrl
          ? <a href={branding.logoLinkUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{logoInner}</a>
          : logoInner
        }
        {!isMobile && <span style={{ color: navText, fontSize: 13, flexShrink: 0, opacity: 0.7 }}>· {title}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, overflowX: 'auto', alignItems: 'center', flexShrink: 1, WebkitOverflowScrolling: 'touch' as any }}>{nav}</div>
      </div>
      <div style={{ flex: 1, maxWidth: 1100, width: '100%', margin: '0 auto', padding: isMobile ? '16px 12px' : '28px 24px', boxSizing: 'border-box' }}>{children}</div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px 24px', borderTop: `1px solid ${C.border}`, background: '#fff', gap: 6 }}>
        <span style={{ fontSize: 14, color: '#000', fontWeight: 500 }}>Powered by</span>
        <a href="https://www.servicetrek.net" target="_blank" rel="noopener noreferrer">
          <img src="/ServiceTrekLogoClear.png" alt="ServiceTrek" style={{ height: 44, width: 'auto' }} />
        </a>
        <a href="/privacy" style={{ fontSize: 12, color: C.textMuted, textDecoration: 'none', marginTop: 4 }}>Privacy Policy</a>
        {branding.supportEmail && (
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6, textAlign: 'center', lineHeight: '1.6' }}>
            <strong style={{ color: C.text }}>Contact Us</strong><br />
            <a href={`mailto:${branding.supportEmail}`} style={{ color: C.textMuted, textDecoration: 'none' }}>{branding.supportEmail}</a>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Live tracker card ─────────────────────────────────────────────────────────
export function TrackerCard({ jobId, contractor, etaMinutes, distanceMiles, connected, connectionError }: { jobId: string; contractor?: any; etaMinutes?: number|null; distanceMiles?: number|null; connected: boolean; connectionError?: string | null }) {
  return (
    <Card style={{ borderLeft: `4px solid ${C.greenMid}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: connectionError ? 4 : 14 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: connectionError ? '#DC2626' : connected ? C.greenMid : C.border, boxShadow: connected ? `0 0 0 3px ${C.greenBg}` : 'none' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: connectionError ? '#DC2626' : connected ? C.greenMid : C.textMuted }}>{connectionError ? 'Live updates unavailable' : connected ? 'Live tracking' : 'Connecting…'}</span>
      </div>
      {connectionError && (
        <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 14 }}>{connectionError} — refresh the page to see the latest status.</div>
      )}
      {contractor && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderTop: `1px solid ${C.borderLight}`, borderBottom: `1px solid ${C.borderLight}`, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: C.green, fontSize: 14 }}>
            {contractor.name.split(' ').map((n: string) => n[0]).join('').slice(0,2)}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{contractor.name}</div>
            <div style={{ fontSize: 12, color: C.textSub }}>{contractor.vehicleDescription} · ★ {contractor.averageRating.toFixed(1)}</div>
          </div>
          {contractor.phoneNumber && (
            <a href={`tel:${contractor.phoneNumber}`} style={{ marginLeft: 'auto', background: C.greenBg, color: C.green, borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: 16 }}>📞</a>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 2 }}>ETA</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.green }}>{etaMinutes != null ? `${etaMinutes} min` : '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 2 }}>Distance</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{distanceMiles != null ? `${distanceMiles.toFixed(1)} mi` : '—'}</div>
        </div>
      </div>
    </Card>
  );
}
